import { Link } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { ArrowLeft, Shield } from "lucide-react";
import { useState } from "react";
import { CONSENT_EVENT, pixelConfigured, setConsent } from "@/lib/fbPixel";

function ConsentChoice() {
  const [done, setDone] = useState("");
  if (!pixelConfigured()) return <p className="text-sm text-zinc-500">(Cet outil n'est pas activé en ce moment.)</p>;
  return (
    <div className="flex flex-wrap gap-2" data-testid="consent-choice">
      <button type="button" className="btn-secondary px-4" onClick={() => { setConsent(false); window.dispatchEvent(new Event(CONSENT_EVENT)); setDone("Refusé : l'outil de mesure est arrêté."); }}>Refuser la mesure</button>
      <button type="button" className="btn-secondary px-4" onClick={() => { setConsent(true); window.dispatchEvent(new Event(CONSENT_EVENT)); setDone("Accepté, merci !"); }}>Accepter la mesure</button>
      {done && <p className="w-full text-sm text-emerald-400">{done}</p>}
    </div>
  );
}

export default function Privacy() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/auth" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-8 transition">
          <ArrowLeft className="w-4 h-4" /> {t.legal?.backToHome || "Retour"}
        </Link>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 grid place-items-center rounded-xl bg-gradient-to-br from-[#FF0050] to-[#8A2BE2]">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-3xl font-black font-display">{t.legal?.privacy || "Politique de confidentialité"}</h1>
        </div>
        <div className="prose prose-invert prose-zinc max-w-none space-y-6 text-zinc-300 leading-relaxed">
          <p className="text-sm text-zinc-500">Dernière mise à jour : 29 octobre 2026</p>

          <h2 className="text-xl font-bold text-white">1. Introduction</h2>
          <p>
            <span translate="no">Metazik</span> (ci-après "la Plateforme") s'engage à protéger la vie privée de ses utilisateurs. La présente
            Politique de confidentialité explique quelles données nous collectons, comment nous les utilisons et les droits
            dont vous disposez concernant vos informations personnelles.
          </p>

          <h2 className="text-xl font-bold text-white">2. Données collectées</h2>
          <p>Nous collectons les catégories de données suivantes :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Informations d'inscription :</strong> nom ou pseudo, adresse email, mot de passe (hashé), rôle choisi (Artiste ou Fan).</li>
            <li><strong>Données de profil :</strong> biographie, avatar, photo de couverture, liens vers réseaux sociaux.</li>
            <li><strong>Contenus publiés :</strong> morceaux audio, pochettes, publications, stories, vidéos de défis.</li>
            <li><strong>Données de transaction :</strong> historique d'achats et de retraits, montants, opérateur Mobile Money et numéro de téléphone utilisé pour payer ou recevoir l'argent (gardé pour pouvoir vous rembourser ou vous verser vos gains en cas de problème, effacé à la suppression du compte).</li>
            <li><strong>Affiliation :</strong> code promo utilisé à l'inscription, et pour les membres du programme leur code et leurs commissions. Le propriétaire d'un code ne voit que des totaux, jamais le détail de ses affiliés.</li>
            <li><strong>Conditions d'utilisation :</strong> la version acceptée et sa date.</li>
            <li><strong>Données d'utilisation :</strong> pages visitées, interactions (likes, commentaires), durée d'écoute.</li>
            <li><strong>Données techniques :</strong> adresse IP, type de navigateur, système d'exploitation, identifiant d'appareil.</li>
          </ul>

          <h2 className="text-xl font-bold text-white">3. Finalités du traitement</h2>
          <p>Vos données sont traitées pour :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Fournir et améliorer les services de la Plateforme.</li>
            <li>Gérer votre compte et personnaliser votre expérience.</li>
            <li>Traiter les paiements et verser les revenus aux artistes.</li>
            <li>Envoyer des notifications liées à votre activité (nouveaux abonnés, achats, ventes, retraits, lives).</li>
            <li>Assurer la sécurité de la Plateforme et prévenir la fraude.</li>
            <li>Respecter nos obligations légales.</li>
          </ul>

          <h2 className="text-xl font-bold text-white">4. Partage des données</h2>
          <p>
            Nous ne vendons jamais vos données personnelles. Nous pouvons partager certaines données avec :
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Prestataires de paiement :</strong> MyPVit et les opérateurs Mobile Money (Airtel Money, Moov Money) pour les paiements et les retraits.</li>
            <li><strong>Meta (Facebook, Instagram) :</strong> uniquement la mesure des publicités (voir « Cookies »).</li>
            <li><strong>Services d'hébergement :</strong> nos serveurs sont hébergés sur des infrastructures sécurisées (Render, Cloudflare).</li>
            <li><strong>Autorités compétentes :</strong> uniquement sur demande légale valide.</li>
          </ul>

          <h2 className="text-xl font-bold text-white">5. Conservation des données</h2>
          <p>
            Vos données sont conservées tant que votre compte est actif. Vous pouvez supprimer votre compte à tout
            moment depuis votre profil (voir <Link to="/delete-account" className="underline">comment supprimer mon compte</Link>) :
            la suppression est immédiate et efface votre profil, votre email, vos publications, commentaires, abonnements,
            playlists, notifications et fichiers envoyés.
          </p>
          <p>
            Restent conservés : l'historique des achats et des paiements (sans numéro de téléphone, email ni nom), pour la
            comptabilité et les obligations légales ; et les morceaux déjà achetés par des fans, qui demeurent dans leur
            bibliothèque avec le nom d'artiste comme crédit, mais ne sont plus en vente. Un solde non retiré au moment de
            la suppression (gains de ventes ou commissions d'affiliation) est perdu, après confirmation explicite.
          </p>

          <h2 className="text-xl font-bold text-white">6. Vos droits</h2>
          <p>Conformément au RGPD et aux lois applicables, vous disposez des droits suivants :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Accès :</strong> obtenir une copie de vos données personnelles.</li>
            <li><strong>Rectification :</strong> corriger des informations inexactes.</li>
            <li><strong>Suppression :</strong> demander l'effacement de vos données.</li>
            <li><strong>Portabilité :</strong> recevoir vos données dans un format structuré.</li>
            <li><strong>Opposition :</strong> refuser certains traitements (marketing direct).</li>
          </ul>
          <p>
            Pour exercer ces droits, contactez-nous à : <strong>equipemetazik@gmail.com</strong>
          </p>

          <h2 className="text-xl font-bold text-white">7. Sécurité</h2>
          <p>
            Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données :
            chiffrement en transit (HTTPS/TLS), hashage des mots de passe, contrôle d'accès strict aux bases de données,
            et audits réguliers de sécurité.
          </p>

          <h2 className="text-xl font-bold text-white">8. Cookies</h2>
          <p>
            <span translate="no">Metazik</span> utilise des cookies essentiels pour le fonctionnement de la Plateforme (authentification, préférences de langue).
            Pour mesurer l'efficacité de ses publicités sur Facebook et Instagram, <span translate="no">Metazik</span> peut utiliser
            l'outil de mesure de Meta (« pixel Meta ») sur le site : il indique à Meta qu'une visite, une inscription (artiste ou fan),
            un achat (son prix) ou une publication vient d'une publicité. Aucune donnée de paiement, aucun numéro de téléphone et
            aucune écoute ne sont transmis à Meta. Le traitement de ces données par Meta est régi par la politique de confidentialité de Meta.
          </p>
          <p>
            Là où la loi le demande (notamment en Europe), cet outil ne démarre qu'après votre accord, donné dans le bandeau
            « Accepter / Refuser ». Ailleurs, il est actif par défaut. Vous pouvez changer d'avis à tout moment :
          </p>
          <ConsentChoice />

          <h2 className="text-xl font-bold text-white">9. Modifications</h2>
          <p>
            Nous pouvons mettre à jour cette politique. En cas de modification substantielle, vous serez informé
            par notification dans l'application. La date de dernière mise à jour figure en haut de ce document.
          </p>

          <h2 className="text-xl font-bold text-white">10. Contact</h2>
          <p>
            Pour toute question relative à la protection de vos données :<br />
            Email : <strong>equipemetazik@gmail.com</strong><br />
            Responsable du traitement : <span translate="no">Metazik</span> SAS
          </p>
        </div>
      </div>
    </div>
  );
}
